import React from "react";
import { Button, Grid, TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";

import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetDdlVendorProfileQuery } from "store/services/vendorProfileApi";

import { useAuth } from "hooks";

import AppSwitch from "components/Shared/AppSwitch";

import { getDateTimeAuto } from "utils/dateFormats";
import { useLazyGetDDLCustomerQuery } from "store/services/customerApi";
import { useLazyGetDDLContractorListQuery } from "store/services/contractorApi";
import {
  useLazyGetContractorPaymentAccountQuery,
  useLazyGetCustomerPaymentAccountQuery,
  useLazyGetVendorPaymentAccountQuery,
} from "store/services/PaymentsApi/paymentAccountApi";

const INITIAL_STATE = {
  account: "",
  entity: "",
  sourceModel: "",
  sourceModelRef: "",
  type: "",
  description: "",
  reference: "",
  amount: "",
  adjustmentDate: getDateTimeAuto(),
  isActive: true,
  paymentAccount: "",
};

const PAYMENT_SOURCE_MODEL = {
  VENDOR_PROFILE: "Vendor Profile",
  CUSTOMER_PROFILE: "Customer Profile",
  CONTRACTOR_PROFILE: "Contractor Profile",
};

const AdjustmentForm = ({
  isBusy = null,
  adjustmentDetail = null,
  onSubmit = () => {},
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account,
    },
  });

  const _watchAccount = useWatch({ control, name: "account" });
  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchSourceModel = useWatch({ control, name: "sourceModel" });
  const _watchSourceModelRef = useWatch({ control, name: "sourceModelRef" });

  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  const [fetchVendors, { data: vendors, isLoading: loadingVendors }] =
    useLazyGetDdlVendorProfileQuery();

  const [fetchCustomers, { data: customers, isLoading: loadingCustomers }] =
    useLazyGetDDLCustomerQuery();

  const [
    fetchContractors,
    { data: contractors, isLoading: loadingContractors },
  ] = useLazyGetDDLContractorListQuery();

  const [
    fetchVendorPaymentAccount,
    {
      data: vendorPaymentRes,
      isLoading: loadingVendorPayment,
      isFetching: fetchingVendorPayment,
      error: vendorPaymentError,
    },
  ] = useLazyGetVendorPaymentAccountQuery();

  const [
    fetchCustomerPaymentAccount,
    {
      data: customerPaymentRes,
      isLoading: loadingCustomerPayment,
      isFetching: fetchingCustomerPayment,
      error: customerPaymentError,
    },
  ] = useLazyGetCustomerPaymentAccountQuery();

  const [
    fetchContractorPaymentAccount,
    {
      data: contractorPaymentRes,
      isLoading: loadingContractorPayment,
      isFetching: fetchingContractorPayment,
      error: contractorPaymentError,
    },
  ] = useLazyGetContractorPaymentAccountQuery();

  // Dynamic options for paymentAccount
  const getPaymentAccountOptions = () => {
    switch (_watchSourceModel) {
      case PAYMENT_SOURCE_MODEL.VENDOR_PROFILE:
        return vendorPaymentRes ?? [];
      case PAYMENT_SOURCE_MODEL.CUSTOMER_PROFILE:
        return customerPaymentRes ?? [];
      case PAYMENT_SOURCE_MODEL.CONTRACTOR_PROFILE:
        return contractorPaymentRes ?? [];
      default:
        return [];
    }
  };

  const isPaymentAccountLoading = () => {
    switch (_watchSourceModel) {
      case PAYMENT_SOURCE_MODEL.VENDOR_PROFILE:
        return loadingVendorPayment || fetchingVendorPayment;
      case PAYMENT_SOURCE_MODEL.CUSTOMER_PROFILE:
        return loadingCustomerPayment || fetchingCustomerPayment;
      case PAYMENT_SOURCE_MODEL.CONTRACTOR_PROFILE:
        return loadingContractorPayment || fetchingContractorPayment;
      default:
        return false;
    }
  };

  const getPaymentAccountError = () => {
    switch (_watchSourceModel) {
      case PAYMENT_SOURCE_MODEL.VENDOR_PROFILE:
        return vendorPaymentError;
      case PAYMENT_SOURCE_MODEL.CUSTOMER_PROFILE:
        return customerPaymentError;
      case PAYMENT_SOURCE_MODEL.CONTRACTOR_PROFILE:
        return contractorPaymentError;
      default:
        return null;
    }
  };

  const handleOnAdjustmentSubmit = (data) => {
    const payload = {
      ...data,
      account: data?.account?._id,
      entity: data?.entity?._id,
      sourceModelRef: data?.sourceModelRef?._id,
      sourceModel:
        data?.sourceModel === PAYMENT_SOURCE_MODEL.VENDOR_PROFILE
          ? "VendorProfile"
          : data?.sourceModel === PAYMENT_SOURCE_MODEL.CUSTOMER_PROFILE
          ? "Customer"
          : "Contractor",
      paymentAccount: data?.paymentAccount?._id,
      amount: Number(data?.amount),
    };
    onSubmit(payload);
  };

  React.useEffect(() => {
    if (!adjustmentDetail) return;
    reset({
      ...adjustmentDetail,
      adjustmentDate: adjustmentDetail?.adjustmentDate
        ? new Date(adjustmentDetail.adjustmentDate).toISOString().slice(0, 16)
        : getDateTimeAuto(),
      amount: adjustmentDetail?.amount?.toString(),
    });
  }, [reset, adjustmentDetail]);

  React.useEffect(() => {
    if (isAccountAdmin || isSystemAdmin) {
      fetchEntities({
        account: _watchAccount?._id ?? viewEntity?.entity?.account?._id,
      });
    }
  }, [
    _watchAccount?._id,
    fetchEntities,
    isAccountAdmin,
    isSystemAdmin,
    viewEntity,
  ]);

  React.useEffect(() => {
    if (!_watchEntity?._id) return;
    fetchVendors({ account: _watchAccount?._id, entity: _watchEntity?._id });
  }, [_watchAccount?._id, _watchEntity?._id, fetchVendors]);

  // Dynamic label for sourceModelRef
  const sourceRefLabel = (() => {
    switch (_watchSourceModel) {
      case PAYMENT_SOURCE_MODEL.VENDOR_PROFILE:
        return "Vendor Profile";
      case PAYMENT_SOURCE_MODEL.CUSTOMER_PROFILE:
        return "Customer Profile";
      case PAYMENT_SOURCE_MODEL.CONTRACTOR_PROFILE:
        return "Contractor Profile";
      default:
        return "Source Profile";
    }
  })();

  // Dynamic options for sourceModelRef
  const sourceRefOptions = React.useMemo(() => {
    switch (_watchSourceModel) {
      case PAYMENT_SOURCE_MODEL.VENDOR_PROFILE:
        return {
          data: vendors || [],
          getOptionLabel: (option) =>
            option ? `${option?.vendor?.name} - ${option?.phone}` : "",
          isLoading: loadingVendors,
        };
      case PAYMENT_SOURCE_MODEL.CUSTOMER_PROFILE:
        return {
          data: customers || [],
          getOptionLabel: (option) =>
            option ? `${option?.customerUser?.name} - ${option?.phone}` : "",
          isLoading: loadingCustomers,
        };
      case PAYMENT_SOURCE_MODEL.CONTRACTOR_PROFILE:
        return {
          data: contractors || [],
          getOptionLabel: (option) =>
            option ? `${option?.name} - ${option?.phone}` : "",
          isLoading: loadingContractors,
        };
      default:
        return { data: [], getOptionLabel: () => "", isLoading: false };
    }
  }, [
    _watchSourceModel,
    vendors,
    customers,
    contractors,
    loadingVendors,
    loadingCustomers,
    loadingContractors,
  ]);

  // Fetch profiles based on sourceModel
  React.useEffect(() => {
    if (!_watchEntity?._id || !_watchAccount?._id || !_watchSourceModel) return;
    if (_watchSourceModel === PAYMENT_SOURCE_MODEL.VENDOR_PROFILE) {
      fetchVendors({ account: _watchAccount?._id, entity: _watchEntity?._id });
    } else if (_watchSourceModel === PAYMENT_SOURCE_MODEL.CUSTOMER_PROFILE) {
      fetchCustomers({
        account: _watchAccount?._id,
        entity: _watchEntity?._id,
      });
    } else if (_watchSourceModel === PAYMENT_SOURCE_MODEL.CONTRACTOR_PROFILE) {
      fetchContractors({
        account: _watchAccount?._id,
        entity: _watchEntity?._id,
      });
    }
  }, [
    _watchAccount?._id,
    _watchEntity?._id,
    _watchSourceModel,
    fetchVendors,
    fetchCustomers,
    fetchContractors,
  ]);

  // Fetch payment accounts based on sourceModel and sourceModelRef
  React.useEffect(() => {
    if (!_watchSourceModel || !_watchSourceModelRef?._id) return;
    if (_watchSourceModel === PAYMENT_SOURCE_MODEL.VENDOR_PROFILE) {
      fetchVendorPaymentAccount({ vendorId: _watchSourceModelRef?._id });
    } else if (_watchSourceModel === PAYMENT_SOURCE_MODEL.CUSTOMER_PROFILE) {
      fetchCustomerPaymentAccount({ customerId: _watchSourceModelRef?._id });
    } else if (_watchSourceModel === PAYMENT_SOURCE_MODEL.CONTRACTOR_PROFILE) {
      fetchContractorPaymentAccount({
        contractorId: _watchSourceModelRef?._id,
      });
    }
  }, [
    _watchSourceModel,
    _watchSourceModelRef?._id,
    fetchVendorPaymentAccount,
    fetchCustomerPaymentAccount,
    fetchContractorPaymentAccount,
  ]);

  // Reset form when editing
  React.useEffect(() => {
    if (!adjustmentDetail) return;
    reset({
      ...adjustmentDetail,
      adjustmentDate: adjustmentDetail?.adjustmentDate
        ? new Date(adjustmentDetail.adjustmentDate).toISOString().slice(0, 16)
        : getDateTimeAuto(),
      sourceModel:
        adjustmentDetail?.sourceModel === "VendorProfile"
          ? PAYMENT_SOURCE_MODEL.VENDOR_PROFILE
          : adjustmentDetail?.sourceModel === "Customer"
          ? PAYMENT_SOURCE_MODEL.CUSTOMER_PROFILE
          : PAYMENT_SOURCE_MODEL.CONTRACTOR_PROFILE,
      sourceModelRef: adjustmentDetail?.sourceModelRef,
      amount: adjustmentDetail?.amount?.toString(),
      paymentAccount: adjustmentDetail?.paymentAccount || "",
    });
  }, [reset, adjustmentDetail]);

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    required
                    label="Account"
                    value={field.value ?? ""}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", "");
                      setValue("vendor", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={accounts || []}
                    disabled={isAccountAdmin}
                    loading={loadingAccounts}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}

          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    required
                    label="Entity"
                    value={field.value ?? ""}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("vendor", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}

          {/* Source Model */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="sourceModel"
              control={control}
              rules={{ required: "Please select a source model." }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  fullWidth
                  required
                  label="Source Model"
                  options={Object.values(PAYMENT_SOURCE_MODEL)}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setValue("sourceModelRef", "");
                    setValue("paymentAccount", "");
                  }}
                  helperText={errors?.sourceModel?.message}
                  error={!!errors?.sourceModel}
                />
              )}
            />
          </Grid>

          {/* Source Model Reference */}
          {_watchSourceModel && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="sourceModelRef"
                control={control}
                rules={{
                  required: `Please select a ${sourceRefLabel.toLowerCase()}.`,
                }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    label={sourceRefLabel}
                    required
                    value={value || ""}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("paymentAccount", "");
                    }}
                    getOptionLabel={sourceRefOptions.getOptionLabel}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={sourceRefOptions.data}
                    loading={sourceRefOptions.isLoading}
                    loadingText={`Loading ${sourceRefLabel}...`}
                    helperText={errors?.sourceModelRef?.message}
                    error={!!errors?.sourceModelRef}
                  />
                )}
              />
            </Grid>
          )}

          {/* Payment Account */}
          {_watchSourceModel && _watchSourceModelRef && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="paymentAccount"
                control={control}
                rules={{ required: "Please select a payment account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Payment Account"
                    value={field.value || ""}
                    onChange={(_, data) => field.onChange(data)}
                    getOptionLabel={(option) => option?.accountName || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={getPaymentAccountOptions()}
                    loading={isPaymentAccountLoading()}
                    loadingText="Loading Payment Accounts..."
                    disabled={!_watchSourceModelRef}
                    error={
                      !!errors.paymentAccount || !!getPaymentAccountError()
                    }
                    helperText={
                      errors.paymentAccount?.message ||
                      (getPaymentAccountError() &&
                        "Failed to load payment accounts") ||
                      (getPaymentAccountOptions().length === 0 &&
                        "No payment accounts available")
                    }
                    noOptionsText="No payment accounts available"
                    renderOption={(props, option) => (
                      <li {...props} key={option._id}>
                        {option.accountName}
                      </li>
                    )}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={6}>
            <Controller
              name="type"
              control={control}
              rules={{ required: "Type is required." }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Type"
                  options={["Credit", "Debit"]}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="reference"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Reference"
                  error={!!errors?.reference}
                  helperText={errors?.reference?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="amount"
              control={control}
              rules={{
                required: "Amount is required",
                pattern: {
                  value: /^[0-9]+(\.[0-9]{1,2})?$/,
                  message: "Enter a valid amount",
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Amount"
                  type="number"
                  error={!!errors?.amount}
                  helperText={errors?.amount?.message}
                  onChange={(e) => {
                    field.onChange(
                      e.target.value === "" ? "" : Number(e.target.value)
                    );
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="adjustmentDate"
              control={control}
              rules={{ required: "Adjustment Date is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Adjustment Date"
                  type="datetime-local"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.adjustmentDate}
                  helperText={errors?.adjustmentDate?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="description"
              control={control}
              rules={{ required: "Description is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  multiline
                  rows={4}
                  size="small"
                  label="Description"
                  error={!!errors?.description}
                  helperText={errors?.description?.message}
                />
              )}
            />
          </Grid>

          {adjustmentDetail && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnAdjustmentSubmit)}
        >
          {adjustmentDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default AdjustmentForm;
